/**
 * CopilotComposer — THE Merlin composer, one component everywhere (Phase 2,
 * kanban 12584b08): tap the mic to talk OR type, plus the inline 📎 clip
 * (drag-drop / paste / picker), Send that becomes Stop while a turn is in
 * flight, and the staged-file chips above the bar.
 *
 * Mounted by the Ask Merlin tab and by the split workspace's conversation
 * pane. The retired triage board carried a second, feature-poor composer
 * (no mic, no attachments, no Stop) — that duplication is what this ends.
 */
import { useEffect, useRef, type ReactNode, type Ref } from 'react';

import { useAudioRecording, type AudioRecordingResult } from '../../hooks/useAudioRecording';
import { DropVeil } from '../chat/AttachmentTray';
import { ACCEPT_ATTR } from '../chat/composerAttachments';
import type { ComposerAttachments } from '../chat/useComposerAttachments';
import styles from './CopilotComposer.module.css';

/**
 * Compact inline push-to-talk mic for the single input bar. Tap to start,
 * tap to stop → hands the recorded blob to `onAudio` (which transcribes +
 * sends), then resets for the next take.
 */
function ComposerMic({
  onAudio,
  disabled,
}: {
  onAudio: (result: AudioRecordingResult) => void;
  disabled: boolean;
}) {
  const {
    state,
    isRecording,
    duration,
    audioBlob,
    mimeType,
    startRecording,
    stopRecording,
    reset,
    isSupported,
  } = useAudioRecording();
  const onAudioRef = useRef(onAudio);
  useEffect(() => {
    onAudioRef.current = onAudio;
  }, [onAudio]);
  // Deliver the recording the moment it stops, then reset for the next take.
  useEffect(() => {
    if (state === 'stopped' && audioBlob && mimeType) {
      onAudioRef.current({ blob: audioBlob, mimeType, duration });
      reset();
    }
  }, [state, audioBlob, mimeType, duration, reset]);
  // No mic in this browser → no button (typing still works).
  if (!isSupported) return null;
  return (
    <button
      type="button"
      className={`${styles.micBtn} ${isRecording ? styles.micBtnRec : ''}`}
      onClick={() => (isRecording ? stopRecording() : startRecording())}
      disabled={disabled || state === 'requesting_permission'}
      aria-label={isRecording ? 'Stop recording and send' : 'Tap to talk'}
      title={isRecording ? 'Recording… tap to stop & send' : 'Tap to talk'}
    >
      {isRecording ? <span className={styles.micStop} aria-hidden="true" /> : '🎤'}
    </button>
  );
}

export interface CopilotComposerProps {
  value: string;
  onChange: (value: string) => void;
  /** Send the typed text (Enter or the Send button). */
  onSubmit: () => void;
  /** A finished voice take — transcribe + send. */
  onAudio: (result: AudioRecordingResult) => void;
  /** Stop the in-flight turn. */
  onStop: () => void;
  busy: boolean;
  transcribing: boolean;
  cancelling: boolean;
  attach: ComposerAttachments;
  /**
   * The composer cannot send at all right now (no conversation on this
   * thread, or it belongs to someone else). The reason shows as the
   * placeholder and every control is disabled.
   */
  disabledReason?: string | null;
  placeholder?: string;
  /** Small print under the bar ("Spoken rundown plays first · …"). */
  footNote?: ReactNode;
  /** Anything to float above the bar (the admin totals badge). */
  extra?: ReactNode;
  /** Tighter chrome for a pane that is already framed (the workspace). */
  compact?: boolean;
  /** The message box, for a host that puts a starter in it and focuses it. */
  inputRef?: Ref<HTMLTextAreaElement>;
}

export function CopilotComposer({
  value,
  onChange,
  onSubmit,
  onAudio,
  onStop,
  busy,
  transcribing,
  cancelling,
  attach,
  disabledReason = null,
  placeholder,
  footNote,
  extra,
  compact = false,
  inputRef,
}: CopilotComposerProps) {
  const locked = !!disabledReason;
  const inputDisabled = locked || busy || transcribing;
  return (
    <div
      className={`${styles.composer} ${compact ? styles.composerCompact : ''}`}
      {...(locked ? {} : attach.dropZoneProps)}
    >
      <DropVeil active={attach.isDragging} />
      {extra}
      {/* Compact chips for any files staged for the next turn. */}
      {attach.files.length > 0 && (
        <div className={styles.photoChips}>
          {attach.files.map((p, i) => (
            <span className={styles.photoChip} key={`${p.name}-${i}`} title={p.name}>
              📎<span className={styles.photoChipName}>{p.name}</span>
              <button
                type="button"
                className={styles.photoChipX}
                aria-label={`Remove ${p.name}`}
                onClick={() => attach.removeFile(i)}
                disabled={busy}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      {attach.attachError && (
        <div className={styles.attachError} role="alert">
          {attach.attachError}
        </div>
      )}

      <div className={styles.bar}>
        {transcribing ? (
          <span className={styles.barStatus}>
            <span className={styles.spinner} /> Transcribing…
          </span>
        ) : (
          <ComposerMic onAudio={onAudio} disabled={busy || locked} />
        )}
        <textarea
          ref={inputRef}
          className={styles.barInput}
          value={value}
          placeholder={
            disabledReason ??
            (busy ? 'Working…' : (placeholder ?? 'Type, or tap the mic to talk…'))
          }
          rows={1}
          disabled={inputDisabled}
          aria-label={placeholder ?? 'Message Merlin'}
          onChange={(e) => onChange(e.target.value)}
          onPaste={attach.onPaste}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (!inputDisabled) onSubmit();
            }
          }}
        />
        <label
          className={styles.clipBtn}
          title="Attach photos or files (or drag & drop / paste)"
        >
          📎
          {attach.files.length > 0 && (
            <span className={styles.clipCount}>{attach.files.length}</span>
          )}
          <input
            type="file"
            accept={ACCEPT_ATTR}
            multiple
            className={styles.fileInput}
            aria-label="Attach photos or files"
            disabled={busy || locked}
            onChange={(e) => {
              attach.addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
        {busy ? (
          // While Merlin works, Send becomes Stop (standard chat pattern):
          // halt the turn, keep the partial reply, stay in the same thread.
          <button
            type="button"
            className={styles.stopTurnBtn}
            onClick={onStop}
            disabled={cancelling}
            title="Stop this response"
            aria-label="Stop this response"
          >
            <span className={styles.stopSquare} aria-hidden="true" />
            {cancelling ? 'Stopping…' : 'Stop'}
          </button>
        ) : (
          <button
            type="button"
            className={styles.sendBtn}
            onClick={onSubmit}
            disabled={locked || (!value.trim() && attach.files.length === 0)}
          >
            Send
          </button>
        )}
      </div>
      {footNote && <p className={styles.foot}>{footNote}</p>}
    </div>
  );
}

export default CopilotComposer;
